import React, { useState, useEffect } from 'react';
import { adminApi, AdminAdDto, AdminAdDetailDto, AdminUpdateAdDto } from '../services/adminApi';
import { toast } from 'react-hot-toast';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  EllipsisVerticalIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  StarIcon,
  FireIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const AdminAdsPage: React.FC = () => {
  const [ads, setAds] = useState<AdminAdDto[]>([]);
  const [pendingAds, setPendingAds] = useState<AdminAdDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedAd, setSelectedAd] = useState<AdminAdDetailDto | null>(null);
  const [showAdModal, setShowAdModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAd, setEditingAd] = useState<AdminUpdateAdDto | null>(null);
  const [editingAdId, setEditingAdId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [adStats, setAdStats] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    if (activeTab === 'all') {
      fetchAds();
    } else if (activeTab === 'pending') {
      fetchPendingAds();
    }
    fetchAdStats();
  }, [currentPage, searchTerm, statusFilter, categoryFilter, activeTab]);

  const fetchAds = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getAds(currentPage, 20, searchTerm, statusFilter, categoryFilter);
      setAds(response.data.ads);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Error loading ads', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
          direction: 'ltr',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingAds = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getPendingAds(currentPage, 20);
      setPendingAds(response.data.ads);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Error loading pending ads', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAdStats = async () => {
    try {
      const response = await adminApi.getAdStats();
      setAdStats(response.data);
    } catch (error) {
      // Stats loading failure is not critical
    }
  };

  const viewAdDetail = async (adId: string) => {
    try {
      const response = await adminApi.getAd(adId);
      setSelectedAd(response.data);
      setShowAdModal(true);
    } catch (error) {
      toast.error('Error loading ad details', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
        }
      });
    }
  };

  const startEditAd = (ad: AdminAdDto) => {
    setEditingAd({
      title: ad.title,
      description: '',
      price: ad.price,
      isNegotiable: false,
      status: ad.status,
      isFeatured: ad.isFeatured,
      isUrgent: ad.isUrgent,
      isHighlighted: false,
      isTopAd: false,
      isPromoted: ad.isPromoted
    });
    setEditingAdId(ad.id);
    setShowEditModal(true);
  };

  const handleUpdateAd = async () => {
    if (!editingAd || !editingAdId) return;

    setActionLoading('update');
    try {
      await adminApi.updateAd(editingAdId, editingAd);
      toast.success('Ad updated successfully', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      setShowEditModal(false);
      setEditingAdId(null);
      fetchAds();
    } catch (error) {
      toast.error('Error updating ad', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleApproveAd = async (adId: string) => {
    setActionLoading(`approve-${adId}`);
    try {
      await adminApi.approveAd(adId);
      toast.success('Ad approved successfully', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      fetchAds();
    } catch (error) {
      toast.error('Error approving ad', {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'iranSans, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectAd = async (adId: string) => {
    let rejectionReason = '';
    
    toast((t) => (
      <div className="flex flex-col gap-3 min-w-80">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
          <span className="font-medium">Reject Ad</span>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rejection Reason:
          </label>
          <textarea
            onChange={(e) => rejectionReason = e.target.value}
            placeholder="Enter rejection reason..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            rows={3}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              if (!rejectionReason.trim()) {
                toast.error('Please enter rejection reason', {
                  icon: '⚠️',
                  style: {
                    background: '#F59E0B',
                    color: '#ffffff',
                    fontFamily: 'Arial, sans-serif',
                    textAlign: 'left',
                    direction: 'ltr',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                  }
                });
                return;
              }
              toast.dismiss(t.id);
              try {
                await adminApi.rejectAd(adId, rejectionReason);
                toast.success('Ad rejected successfully', {
                  icon: '🚫',
                  style: {
                    background: '#10B981',
                    color: '#ffffff',
                    fontFamily: 'Arial, sans-serif',
                    textAlign: 'left',
                    direction: 'ltr',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                  }
                });
                fetchAds();
              } catch (error) {
                                  toast.error('Error rejecting ad', {
                  icon: '❌',
                  style: {
                    background: '#EF4444',
                    color: '#ffffff',
                    fontFamily: 'iranSans, sans-serif',
                    textAlign: 'right',
                    direction: 'rtl',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                  }
                });
              }
            }}
            className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Reject Ad
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: 'white',
        color: 'black',
        border: '1px solid #e5e7eb',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        borderRadius: '0.75rem',
        padding: '1rem',
      }
    });
  };

  const handleDeleteAd = async (adId: string) => {
    toast((t) => (
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
          <span className="font-medium">Delete Ad</span>
        </div>
                  <p className="text-sm text-gray-600">Are you sure you want to delete this ad?</p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await adminApi.deleteAd(adId);
                toast.success('Ad deleted successfully', {
                  icon: '🗑️',
                  style: {
                    background: '#10B981',
                    color: '#ffffff',
                    fontFamily: 'iranSans, sans-serif',
                    textAlign: 'right',
                    direction: 'rtl',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                  }
                });
                fetchAds();
              } catch (error) {
                toast.error('Error deleting ad', {
                  icon: '❌',
                  style: {
                    background: '#EF4444',
                    color: '#ffffff',
                    fontFamily: 'iranSans, sans-serif',
                    textAlign: 'right',
                    direction: 'rtl',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    padding: '16px',
                  }
                });
              }
            }}
            className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
          >
            Delete
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      style: {
        background: 'white',
        color: 'black',
        border: '1px solid #e5e7eb',
        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
        borderRadius: '0.75rem',
        padding: '1rem',
      }
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            <CheckCircleIcon className="w-3 h-3" />
            Active
          </span>
        );
      case 'pendingapproval':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
            <ClockIcon className="w-3 h-3" />
            Pending Approval
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
            <XCircleIcon className="w-3 h-3" />
            Rejected
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
            <ExclamationTriangleIcon className="w-3 h-3" />
            Expired
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
            {status}
          </span>
        );
    }
  };

  const currentAds = activeTab === 'all' ? ads : pendingAds;

  const filteredAds = currentAds.filter(ad => {
    const matchesStatus = statusFilter === 'all' || 
      ad.status.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Modern Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Ads</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage all ads published in the system
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                <ArrowDownTrayIcon className="w-4 h-4" />
                Export
              </button>
              <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors">
                <PlusIcon className="w-4 h-4" />
                New Ad
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        {adStats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <div key="total-ads" className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Total Ads</p>
                  <p className="text-2xl font-semibold text-gray-900">{(adStats.totalAds || 0).toLocaleString()}</p>
                                      <p className="text-xs text-green-600 font-medium">+18% from last month</p>
                </div>
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📝</span>
                </div>
              </div>
            </div>
            
            <div key="active-ads" className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Active</p>
                  <p className="text-2xl font-semibold text-gray-900">{(adStats.activeAds || 0).toLocaleString()}</p>
                                      <p className="text-xs text-green-600 font-medium">+12% from last month</p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                  <span className="text-xl">✅</span>
                </div>
              </div>
            </div>
            
            <div key="pending-ads" className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Pending</p>
                  <p className="text-2xl font-semibold text-gray-900">{(adStats.pendingAds || 0).toLocaleString()}</p>
                                      <p className="text-xs text-yellow-600 font-medium">+5% from last month</p>
                </div>
                <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center">
                  <span className="text-xl">⏳</span>
                </div>
              </div>
            </div>
            
            <div key="featured-ads" className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Featured</p>
                  <p className="text-2xl font-semibold text-gray-900">{(adStats.featuredAds || 0).toLocaleString()}</p>
                                      <p className="text-xs text-purple-600 font-medium">+25% from last month</p>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-lg flex items-center justify-center">
                  <span className="text-xl">⭐</span>
                </div>
              </div>
            </div>
            
            <div key="recent-ads" className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">This Month</p>
                  <p className="text-2xl font-semibold text-gray-900">{(adStats.recentAds || 0).toLocaleString()}</p>
                                      <p className="text-xs text-green-600 font-medium">+30% from last month</p>
                </div>
                <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center">
                  <span className="text-xl">📈</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modern Tabs */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('all')}
                className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'all'
                    ? 'border-primary-500 text-primary-600 bg-primary-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                All Ads ({adStats?.totalAds || ads.length})
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'pending'
                    ? 'border-yellow-500 text-yellow-600 bg-yellow-50'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Approval ({adStats?.pendingAds || pendingAds.length})
              </button>
            </nav>
          </div>

          {/* Modern Search and Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by title, category or user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-11 pl-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
              </div>
              
              <div className="flex gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white min-w-[120px]"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pendingapproval">Pending</option>
                  <option value="rejected">Rejected</option>
                  <option value="expired">Expired</option>
                </select>
                
                <input
                  type="text"
                  placeholder="Category"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[120px]"
                />
                
                <button className="inline-flex items-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">
                  <FunnelIcon className="w-4 h-4" />
                  Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Ads Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Ads List ({filteredAds.length} ads)
              </h3>
              <div className="text-sm text-gray-500">
                Page {currentPage} of {pagination?.totalPages || 1}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading ads...</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Title
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Price
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Category
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        User
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Status
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Views
                      </th>
                      <th className="text-left py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Post Date
                      </th>
                      <th className="text-center py-4 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAds.map((ad, index) => (
                      <tr key={ad.id} className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div>
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                  {ad.title}
                                </div>
                                {ad.isFeatured && (
                                  <StarIcon className="w-4 h-4 text-yellow-500" title="Featured" />
                                )}
                                {ad.isUrgent && (
                                  <FireIcon className="w-4 h-4 text-red-500" title="Urgent" />
                                )}
                                {ad.isPromoted && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Promoted</span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">{ad.city}, {ad.province}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {ad.price.toLocaleString()} {ad.currency || 'CAD'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{ad.categoryName}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{ad.userName}</div>
                              <div className="text-sm text-gray-500">{ad.userEmail}</div>
                            </div>
                            {ad.userIsVerified && (
                              <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2" title="Verified User" />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(ad.status)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {ad.viewCount.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {new Date(ad.datePosted).toLocaleDateString('en-US')}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => viewAdDetail(ad.id)}
                              className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => startEditAd(ad)}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <PencilIcon className="w-4 h-4" />
                            </button>
                            {ad.status === 'PendingApproval' && (
                              <>
                                <button
                                  onClick={() => handleApproveAd(ad.id)}
                                  disabled={actionLoading === `approve-${ad.id}`}
                                  className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Approve"
                                >
                                  {actionLoading === `approve-${ad.id}` ? (
                                    <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <CheckCircleIcon className="w-4 h-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleRejectAd(ad.id)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Reject"
                                >
                                  <XCircleIcon className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDeleteAd(ad.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}
              {pagination && (
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * 20, pagination.totalAds || 0)}</span> of{' '}
                      <span className="font-medium">{pagination.totalAds || 0}</span> ads
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      
                      <div className="flex items-center gap-1">
                        {[...Array(Math.min(5, pagination.totalPages || 1))].map((_, i) => {
                          const pageNum = i + 1;
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-primary-600 text-white'
                                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(pagination.totalPages || 1, currentPage + 1))}
                        disabled={currentPage === (pagination.totalPages || 1)}
                        className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Enhanced Ad Detail Modal */}
      {showAdModal && selectedAd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-8 mx-auto max-w-4xl">
            <div className="bg-white rounded-xl shadow-2xl">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Ad Details</h3>
                  <p className="text-sm text-gray-500 mt-1">{selectedAd.title}</p>
                </div>
                <button
                  onClick={() => setShowAdModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Ad Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Ad Information</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedAd.title}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">
                        {selectedAd.price.toLocaleString()} {selectedAd.currency || 'CAD'}
                      </p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">{selectedAd.categoryName}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <div className="bg-gray-50 p-2 rounded-lg">
                          {getStatusBadge(selectedAd.status)}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded-lg">
                        {selectedAd.city}, {selectedAd.province}
                      </p>
                    </div>

                    {/* Ad Images */}
                    {selectedAd.images && selectedAd.images.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Ad Images ({selectedAd.images.length})</label>
                        <div className="grid grid-cols-2 gap-2">
                          {selectedAd.images.map((image, index) => (
                            <div key={image.id} className="relative group">
                              <img
                                src={image.imageUrl}
                                alt={`Image ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border border-gray-200"
                              />
                              <div 
                                className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center cursor-pointer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('Image clicked:', image.imageUrl);
                                  console.log('Image object:', image);
                                  // Simple and reliable approach to open image
                                  const link = document.createElement('a');
                                  link.href = image.imageUrl;
                                  link.target = '_blank';
                                  link.rel = 'noopener noreferrer';
                                  link.click();
                                }}
                              >
                                <svg className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Click on an image to view it in full size</p>
                      </div>
                    )}
                  </div>

                  {/* Stats and Actions */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">Stats and Actions</h4>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Views:</span>
                        <span className="text-sm text-gray-900">{selectedAd.viewCount.toLocaleString()}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Post Date:</span>
                        <span className="text-sm text-gray-900">{new Date(selectedAd.datePosted).toLocaleDateString('en-US')}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Last Update:</span>
                        <span className="text-sm text-gray-900">
                                                      {selectedAd.dateUpdated ? new Date(selectedAd.dateUpdated).toLocaleDateString('en-US') : 'None'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">User:</span>
                        <span className="text-sm text-gray-900">{selectedAd.userName}</span>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        {selectedAd.isFeatured && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">Featured</span>
                        )}
                        {selectedAd.isUrgent && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">Urgent</span>
                        )}
                        {selectedAd.isPromoted && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Promoted</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowAdModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      startEditAd(selectedAd);
                      setShowAdModal(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Edit Ad
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ad Modal */}
      {showEditModal && editingAd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-8 mx-auto max-w-2xl">
            <div className="bg-white rounded-xl shadow-2xl">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Edit Ad</h3>
                  <p className="text-sm text-gray-500 mt-1">Edit ad information</p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ad Title</label>
                    <input
                      type="text"
                      value={editingAd.title}
                      onChange={(e) => setEditingAd({...editingAd, title: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Price</label>
                    <input
                      type="number"
                      value={editingAd.price}
                      onChange={(e) => setEditingAd({...editingAd, price: Number(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={editingAd.status}
                      onChange={(e) => setEditingAd({...editingAd, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="Active">Active</option>
                      <option value="PendingApproval">Pending Approval</option>
                      <option value="Rejected">Rejected</option>
                      <option value="Expired">Expired</option>
                    </select>
                  </div>

                  {/* Features */}
                  <div className="border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-medium text-gray-900 mb-4">Ad Features</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingAd.isFeatured}
                          onChange={(e) => setEditingAd({...editingAd, isFeatured: e.target.checked})}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label className="mr-2 block text-sm text-gray-900">Featured Ad</label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingAd.isUrgent}
                          onChange={(e) => setEditingAd({...editingAd, isUrgent: e.target.checked})}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label className="mr-2 block text-sm text-gray-900">Urgent</label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingAd.isPromoted}
                          onChange={(e) => setEditingAd({...editingAd, isPromoted: e.target.checked})}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label className="mr-2 block text-sm text-gray-900">Promoted</label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingAd.isNegotiable}
                          onChange={(e) => setEditingAd({...editingAd, isNegotiable: e.target.checked})}
                          className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <label className="mr-2 block text-sm text-gray-900">Negotiable</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateAd}
                    disabled={actionLoading === 'update'}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === 'update' ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Saving...
                      </>
                    ) : (
                                              'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAdsPage; 
